<template>
  <div class="expandad-image-container bg-white">
    <div class="header mb-4 flex items-center justify-between">
      <div class="toggle select-none">
        <input :id="`toggle-${toggleId}`" v-model="isShowingOriginal" type="checkbox" class="toggleCheckbox" />

        <label :for="`toggle-${toggleId}`" class="toggleContainer">
          <div class="text-base">{{ t("pages.sell-a-profile.preview-gallery.edited") }}</div>

          <div class="text-base">{{ t("pages.sell-a-profile.preview-gallery.originals") }}</div>
        </label>
      </div>

      <img :src="CloseIcon.src" class="close-button cursor-pointer" @click="emit('close')" />
    </div>

    <div class="image-container relative">
      <div
        class="opposite-image absolute flex items-center justify-center"
        :class="{ active: shouldShowOppositeImage }"
      >
        <div v-if="oppositeStatus === 'PROCESSING'" class="flex flex-col items-center justify-center">
          <IAILoader :variant="'PULSE'" :color="'disabled'" />

          <div class="mt-4 flex flex-col items-center text-base font-normal">
            <div>{{ fileName }}</div>
            {{ t("pages.sell-a-profile.preview.gallery.processing") }}
          </div>
        </div>

        <div v-else-if="oppositeStatus === 'FAIL'" class="flex flex-col items-center justify-center">
          <img :src="Icons.Cancel.src" />

          <div class="mt-4 text-base font-normal">{{ t("pages.sell-a-profile.preview.gallery.error") }}</div>
        </div>

        <img v-else :src="props.oppositePresignedUrl" alt="Expanded Image" />
      </div>

      <img class="source-image" :src="props.presignedUrl" alt="Expanded Image" />
    </div>
  </div>
</template>

<script setup lang="ts">
import CloseIcon from "src/core-ui-vue/components/IAIIcon/icons/Close.svg";
import { PropType, ref, computed, watch } from "vue";
import { Status } from "./GalleryItem.vue";
import { IAILoader } from "@/core-ui-vue";
import { t } from "@/locales/vue";
import { Icons } from "@/core-ui-vue";
import { useMagicKeys } from "@vueuse/core";
import { GalleryType } from "@/features/sell-a-profile/components/bundle-details/ProfilePreviewGallery.vue";

const props = defineProps({
  presignedUrl: { type: String, required: true },
  oppositePresignedUrl: { type: [String, undefined] as PropType<string | undefined>, required: true },
  oppositeStatus: { type: String as PropType<Status>, required: true },
  fileName: { type: String, required: true },
  isShown: { type: Boolean, required: true },
  type: { type: String as PropType<GalleryType>, required: true },
});

const emit = defineEmits<{
  (event: "close"): void;
}>();

const toggleId = crypto.randomUUID();
const isShowingOriginal = ref(props.type === "ORIGINAL");

useMagicKeys({
  onEventFired: (e: KeyboardEvent) => {
    if (e.type !== "keydown") return;

    checkForToggle(e);
  },
});

const checkForToggle = (e: KeyboardEvent) => {
  if (!props.isShown) return;
  if (e.key === "ArrowLeft") {
    isShowingOriginal.value = false;
  } else if (e.key === "ArrowRight") {
    isShowingOriginal.value = true;
  }
};

const shouldShowOppositeImage = computed(() =>
  props.type === "ORIGINAL" ? !isShowingOriginal.value : isShowingOriginal.value
);

watch(
  () => props.isShown,
  () => {
    isShowingOriginal.value = props.type === "ORIGINAL";
  }
);
</script>

<style scoped lang="scss">
.opposite-image {
  height: -webkit-fill-available;
  width: -webkit-fill-available;
  @apply bg-theme-placeholder-bg text-xs text-theme-1;
  opacity: 0;
  transition: all 0.3s ease-in-out;

  &.active {
    opacity: 1;
  }
}

.expandad-image-container {
  padding: 1rem 1.25rem;
  max-width: 80%;
  max-height: 90%;
  position: relative;
  height: 100%;

  .image-container {
    height: calc(100% - 80px);
  }
}

img {
  height: 100%;
}

.toggleContainer {
  position: relative;
  align-items: center;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  background: #f6f6f6;
  border-radius: 2px;
  color: #0c1b33;
  cursor: pointer;
  height: 44px;
  width: 340px;
}
.toggleContainer::before {
  content: "";
  position: absolute;
  width: calc(50% - 8px);
  height: 80%;
  margin: 4px;
  left: 0%;
  background: white;
  box-shadow: 0px 0px 2px 0px rgba(12, 27, 51, 0.15);
  transition: all 0.3s;
}
.toggleCheckbox:checked + .toggleContainer::before {
  left: 50%;
}
.toggleContainer div {
  padding: 6px;
  text-align: center;
  z-index: 1;
}
.toggleCheckbox {
  display: none;
  .toggleContainer div {
    color: #0c1b33;
    transition: color 0.3s;
  }
}
.toggleCheckbox:not(:checked) + .toggleContainer div:first-child {
  font-weight: 500;
}
.toggleCheckbox:checked + .toggleContainer div:last-child {
  font-weight: 500;
}
</style>
